import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { toast } from "@/providers/NotificationProvider";
import { useAuth } from "@/providers/AuthProvider";
import { agentSpecsAPI } from "@/services/api/agentSpecs";
import { deploymentsAPI } from "@/services/api/deployments";
import { useContextStore } from "@/store/context";
import { useOperateStore } from "@/store/operate";
import { agentSpecAuthoringHref } from "@/utils/contextUrl";

export default function CreateSandboxAgentDialog({
  open,
  onOpenChange,
  organizationId,
  suggestedName = "",
  sourceAgentId = "",
  sourceThreadId = "",
  sourceRevisionId = "",
  continuePreviewResourceId = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  suggestedName?: string;
  sourceAgentId?: string;
  sourceThreadId?: string;
  sourceRevisionId?: string;
  continuePreviewResourceId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const setDeploymentId = useContextStore((state) => state.setDeploymentId);
  const setWorkspace = useContextStore((state) => state.setWorkspace);
  const setDeployments = useContextStore((state) => state.setDeployments);
  const setCurrentSessionId = useOperateStore(
    (state) => state.setCurrentSessionId,
  );
  const [name, setName] = useState(suggestedName);
  const [creating, setCreating] = useState(false);
  const canSeed =
    Boolean(sourceAgentId.trim()) &&
    Boolean(sourceThreadId.trim() || sourceRevisionId.trim());

  useEffect(() => {
    if (open) {
      setName(suggestedName);
    }
  }, [open, suggestedName]);

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!organizationId.trim()) {
      toast.error(t("contextSwitcher.createDeploymentNoOrganization"));
      return;
    }
    if (!trimmed) {
      toast.error(t("agentSpec.previewCreateSandboxNameRequired"));
      return;
    }
    setCreating(true);
    try {
      const deployment = await deploymentsAPI.createDeployment(organizationId, {
        name: trimmed,
        environment: "development",
        deployment_kind: "sandbox",
      });
      await refreshProfile();
      const contextState = useContextStore.getState();
      if (!contextState.deployments.some((item) => item.id === deployment.id)) {
        setDeployments([...contextState.deployments, deployment]);
      }
      setDeploymentId(deployment.id);
      setWorkspace(null);
      setCurrentSessionId(null);

      if (canSeed) {
        const seeded = await agentSpecsAPI.seedStudioFrom(
          organizationId,
          deployment.id,
          {
            source_agent_id: sourceAgentId.trim(),
            source_thread_id: sourceThreadId.trim() || undefined,
            source_revision_id: sourceRevisionId.trim() || undefined,
            title: t("agentSpec.previewCreateSandboxSeedTitle", {
              name: deployment.name,
            }),
          },
        );
        const next = new URLSearchParams();
        next.set("organizationId", organizationId);
        next.set("deploymentId", deployment.id);
        next.set("threadId", seeded.thread.id);
        if (seeded.revision?.id) {
          next.set("revisionId", seeded.revision.id);
        }
        const continueId = continuePreviewResourceId.trim();
        if (continueId) {
          next.set("continuePreview", continueId);
        }
        onOpenChange(false);
        navigate(`/studio/chat?${next.toString()}`);
        toast.success(
          t("agentSpec.previewCreateSandboxSeedSuccess", {
            name: deployment.name,
          }),
        );
        return;
      }

      onOpenChange(false);
      navigate(agentSpecAuthoringHref(organizationId, deployment.id));
      toast.success(
        t("agentSpec.previewCreateSandboxSuccess", { name: deployment.name }),
      );
    } catch (createError) {
      const errorMessage =
        createError instanceof Error ? createError.message : "";
      const is409 =
        errorMessage.includes("409") ||
        errorMessage.includes("Conflict") ||
        errorMessage.includes("already exists");
      toast.error(
        is409
          ? t("agentSpec.previewCreateSandboxConflict")
          : errorMessage || t("agentSpec.previewCreateSandboxFailed"),
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!creating) onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {canSeed
              ? t("agentSpec.previewCreateSandboxSeedDialogTitle")
              : t("agentSpec.previewCreateSandboxTitle")}
          </DialogTitle>
          <DialogDescription>
            {canSeed
              ? t("agentSpec.previewCreateSandboxSeedDescription")
              : t("agentSpec.previewCreateSandboxDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <label className="text-sm font-medium">
            {t("agentSpec.previewCreateSandboxNameLabel")}
          </label>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("agentSpec.previewCreateSandboxNamePlaceholder")}
            disabled={creating}
            data-testid="preview-sandbox-name-input"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleCreate()}
            disabled={creating}
            data-testid="preview-sandbox-create-submit"
          >
            {creating
              ? t("agentSpec.previewCreateSandboxSubmitting")
              : canSeed
                ? t("agentSpec.previewCreateSandboxSeedConfirm")
                : t("agentSpec.previewCreateSandboxConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
